import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { EnvelopeIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import { PRODUCT_NAME } from '../config/brand';

const RegisterPage = () => {
    const navigate = useNavigate();
    const { t } = useLanguage();
    const { register } = useAuth();

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    const styles = {
        container: {
            minHeight: 'calc(100vh - 56px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '2rem',
        },
        card: {
            width: '100%',
            maxWidth: '400px',
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            borderRadius: '24px',
            padding: '2.5rem',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
        },
        title: {
            fontSize: '1.75rem',
            fontWeight: '700',
            color: '#1D1D1F',
            marginBottom: '0.5rem',
            textAlign: 'center',
        },
        subtitle: {
            fontSize: '0.9rem',
            color: '#86868B',
            marginBottom: '2rem',
            textAlign: 'center',
        },
        inputGroup: {
            marginBottom: '1rem',
        },
        inputWrapper: {
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
        },
        inputIcon: {
            position: 'absolute',
            left: '14px',
            width: '20px',
            height: '20px',
            color: '#86868B',
        },
        input: {
            width: '100%',
            padding: '14px 14px 14px 44px',
            border: '1px solid rgba(0, 0, 0, 0.1)',
            borderRadius: '12px',
            fontSize: '0.95rem',
            outline: 'none',
            transition: 'border-color 0.2s ease',
            backgroundColor: '#fff',
        },
        submitButton: {
            width: '100%',
            padding: '14px',
            backgroundColor: '#007AFF',
            color: '#fff',
            border: 'none',
            borderRadius: '12px',
            fontSize: '1rem',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            marginTop: '0.5rem',
        },
        submitButtonDisabled: {
            backgroundColor: '#E5E5EA',
            color: '#86868B',
            cursor: 'not-allowed',
        },
        error: {
            backgroundColor: 'rgba(255, 59, 48, 0.1)',
            color: '#FF3B30',
            padding: '12px',
            borderRadius: '10px',
            fontSize: '0.85rem',
            marginBottom: '1rem',
            textAlign: 'center',
        },
        links: {
            marginTop: '1.5rem',
            textAlign: 'center',
            fontSize: '0.85rem',
        },
        link: {
            color: '#007AFF',
            textDecoration: 'none',
            fontWeight: '500',
        },
    };

    const validateEmail = (email) => {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!email) {
            setError(t('auth.emailRequired'));
            return;
        }
        if (!validateEmail(email)) {
            setError(t('auth.invalidEmail'));
            return;
        }
        if (!password) {
            setError(t('auth.passwordRequired'));
            return;
        }
        if (password.length < 8) {
            setError(t('auth.passwordTooShort'));
            return;
        }
        if (password !== confirmPassword) {
            setError(t('auth.passwordMismatch'));
            return;
        }

        setIsLoading(true);
        try {
            await register(email, password);
            navigate('/');
        } catch (err) {
            setError(err.response?.data?.message || t('auth.error'));
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div style={styles.container}>
            <div style={styles.card}>
                <h1 style={styles.title}>{t('auth.register')}</h1>
                <p style={styles.subtitle}>{PRODUCT_NAME}</p>

                {error && <div style={styles.error}>{error}</div>}

                <form onSubmit={handleSubmit}>
                    <div style={styles.inputGroup}>
                        <div style={styles.inputWrapper}>
                            <EnvelopeIcon style={styles.inputIcon} />
                            <input
                                type="email"
                                placeholder={t('auth.email')}
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                style={styles.input}
                            />
                        </div>
                    </div>

                    <div style={styles.inputGroup}>
                        <div style={styles.inputWrapper}>
                            <LockClosedIcon style={styles.inputIcon} />
                            <input
                                type="password"
                                placeholder={t('auth.password')}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                style={styles.input}
                            />
                        </div>
                    </div>

                    <div style={styles.inputGroup}>
                        <div style={styles.inputWrapper}>
                            <LockClosedIcon style={styles.inputIcon} />
                            <input
                                type="password"
                                placeholder={t('auth.confirmPassword')}
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                style={styles.input}
                            />
                        </div>
                    </div>

                    <button
                        type="submit"
                        disabled={isLoading}
                        style={{
                            ...styles.submitButton,
                            ...(isLoading ? styles.submitButtonDisabled : {}),
                        }}
                    >
                        {isLoading ? t('auth.registering') : t('auth.register')}
                    </button>
                </form>

                <div style={styles.links}>
                    <span style={{ color: '#86868B' }}>{t('auth.hasAccount')} </span>
                    <Link to="/login" style={styles.link}>{t('auth.goToLogin')}</Link>
                </div>
            </div>
        </div>
    );
};

export default RegisterPage;
